import { Box, IconButton, List, ListItemButton, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { colors } from "../../../theme/colors";

export const SIDEBAR_ICON_SIZE = 20;
export const SIDEBAR_ICON_CONTAINER_SIZE = 36;

export const SIDEBAR_COLLAPSED_WIDTH = 100;
export const SIDEBAR_EXPANDED_WIDTH = 280;
export const SIDEBAR_TRANSITION_MS = 350;
export const SIDEBAR_TRANSITION = `width ${SIDEBAR_TRANSITION_MS}ms ease-in-out`;

export const ExpandedContainer = styled(Box)({
  position: "relative",
});

export const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "44px 32px 32px 32px",
  width: expanded
    ? `${SIDEBAR_EXPANDED_WIDTH}px`
    : `${SIDEBAR_COLLAPSED_WIDTH}px`,
  height: "100vh",
  position: "fixed",
  top: 0,
  left: 0,
  background: colors.background.secondary,
  zIndex: expanded ? 1100 : 1000,
  transition: SIDEBAR_TRANSITION,
}));

export const SidebarContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  overflow: "hidden",
});

export const SidebarBackdrop = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  position: "fixed",
  top: 0,
  left: expanded
    ? `${SIDEBAR_EXPANDED_WIDTH}px`
    : `${SIDEBAR_COLLAPSED_WIDTH}px`,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  zIndex: 1050,
  opacity: expanded ? 1 : 0,
  pointerEvents: expanded ? "auto" : "none",
  transition: `left ${SIDEBAR_TRANSITION_MS}ms ease-in-out, opacity ${SIDEBAR_TRANSITION_MS}ms ease-in-out`,
}));

export const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  marginBottom: "60px",
  paddingLeft: expanded ? "0" : "2.5px",
  display: "flex",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
}));

export const LogoMark = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  position: "relative",
  width: expanded ? 188 : 35,
  height: 40,
  flexShrink: 0,
  transition: SIDEBAR_TRANSITION,
}));

export const LogoVariant = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded" && prop !== "variant",
})<{ expanded?: boolean; variant: "full" | "small" }>(({ expanded, variant }) => {
  const isVisible = variant === "full" ? Boolean(expanded) : !expanded;

  return {
    position: "absolute",
    left: 0,
    top: 0,
    opacity: isVisible ? 1 : 0,
    transition: `opacity ${SIDEBAR_TRANSITION_MS * 0.4}ms ease-in-out`,
    transitionDelay:
      variant === "full" && expanded ? `${SIDEBAR_TRANSITION_MS * 0.35}ms` : "0ms",
    pointerEvents: isVisible ? "auto" : "none",
  };
});

export const ToggleButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  transform: expanded
    ? "translateX(50%) rotate(180deg)"
    : "translateX(50%) rotate(0deg)",
  position: "absolute",
  top: "43px",
  right: 0,
  zIndex: 2,
  marginBottom: "16px",
  alignSelf: "flex-start",
  padding: "6.5px 1.5px",
  flexShrink: 0,
  backgroundColor: colors.background.secondary,
  border: `1px solid ${colors.background.mediumBlue}`,
  borderRadius: 125,
  transition: `transform ${SIDEBAR_TRANSITION_MS}ms ease-in-out, background-color 0.2s ease`,
  "&:hover": {
    backgroundColor: colors.background.lightBlue,
  },
}));

export const LogoLink = styled(Link)({
  display: "flex",
  alignItems: "center",
  width: "100%",
  color: "inherit !important",
  fontWeight: "inherit !important",
  fontSize: "inherit !important",
  lineHeight: "inherit !important",
  textDecoration: "none !important",
  outline: "none !important",
  "&:focus, &:focus-visible": {
    outline: "none !important",
  },
  "&:hover": {
    opacity: "1 !important",
  },
});

export const SidebarMenuButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  padding: "5px 0",
  gap: expanded ? 16 : 0,
  maxHeight: SIDEBAR_ICON_CONTAINER_SIZE,
  width: expanded ? "100%" : SIDEBAR_ICON_CONTAINER_SIZE,
  borderRadius: "8px",
  overflow: "hidden",
  flexShrink: 0,
  transition: `width ${SIDEBAR_TRANSITION_MS}ms ease-in-out, gap ${SIDEBAR_TRANSITION_MS}ms ease-in-out`,
}));

export const SidebarItemLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded?: boolean }>(({ expanded }) => ({
  flex: expanded ? "1 1 auto" : "0 0 0px",
  width: expanded ? "auto" : 0,
  minWidth: 0,
  maxWidth: expanded ? "100%" : 0,
  opacity: expanded ? 1 : 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  transition: expanded
    ? `opacity ${SIDEBAR_TRANSITION_MS * 0.4}ms ease-in-out ${SIDEBAR_TRANSITION_MS * 0.35}ms, max-width ${SIDEBAR_TRANSITION_MS}ms ease-in-out`
    : `opacity ${SIDEBAR_TRANSITION_MS * 0.25}ms ease-in-out, max-width ${SIDEBAR_TRANSITION_MS}ms ease-in-out`,
  pointerEvents: expanded ? "auto" : "none",
}));

export const sidebarListItemIconSx = {
  minWidth: SIDEBAR_ICON_CONTAINER_SIZE,
  width: SIDEBAR_ICON_CONTAINER_SIZE,
  height: SIDEBAR_ICON_CONTAINER_SIZE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box" as const,
  flexShrink: 0,
};

export const NavList = styled(List)({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 18,
  alignItems: "flex-start",
  flexShrink: 0,
});

export const LogoutContainer = styled(Box)({
  marginTop: "auto",
  whiteSpace: "nowrap",
  flexShrink: 0,
});

